import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, Empty, fmtDate } from '../../components/ui';
import { PO_CHIP, PO_STATE, qtyOf, type PoState } from './Dashboard';

/* ============================================================
   Purchase order — daftar mentah.

   Kolom terobosan berdiri sendiri dan tidak disembunyikan di balik
   ikon. PO yang terbit tanpa MAR yang disetujui adalah keputusan
   komersial yang ditandatangani seseorang, dan daftar yang harus
   dibuka satu per satu untuk menemukannya tidak akan pernah
   ditinjau.
   ============================================================ */

export type ProcOrder = {
  id: string;
  packageId: string;
  packageCode: string;
  poNo: string;
  state: PoState;
  issuedOn: string | null;
  qty: number;
  unit: string;
  supplierName: string | null;
  overridden: boolean;
  overrideReason: string | null;
  deliveredQty: number;
  shortfallQty: number;
  pct: number;
  noteCount: number;
};

export function Orders({ projectId }: { projectId: string }) {
  const nav = useNavigate();
  const [orders, setOrders] = useState<ProcOrder[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [state, setState] = useState('');
  const [shortOnly, setShortOnly] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ orders: ProcOrder[] }>(`/proc/orders?projectId=${projectId}`);
      setOrders(r.orders);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load purchase orders.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!orders) return <Loading rows={4} />;

  const q = search.trim().toLowerCase();
  const shown = orders.filter((o) => {
    if (state && o.state !== state) return false;
    if (shortOnly && !(o.qty > 0 && o.shortfallQty > 0)) return false;
    if (!q) return true;
    return [o.poNo, o.packageCode, o.supplierName ?? ''].some((v) => v.toLowerCase().includes(q));
  });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Purchase order</h1>
          <p>Setiap surat jalan ditagih terhadap kuantitas PO, bukan terhadap kuantitas paket.</p>
        </div>
      </div>

      <div className="filters">
        <input
          className="input"
          placeholder="Cari nomor PO, paket, atau pemasok"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="select" value={state} onChange={(e) => setState(e.target.value)}>
          <option value="">Semua status</option>
          <option value="ISSUED">{PO_STATE.ISSUED}</option>
          <option value="CANCELLED">{PO_STATE.CANCELLED}</option>
        </select>
        <label className="hint" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={shortOnly} onChange={(e) => setShortOnly(e.target.checked)} />
          Hanya yang kurang kirim
        </label>
      </div>

      {orders.length === 0 ? (
        <Empty
          title="Belum ada purchase order"
          body="PO diterbitkan dari halaman paket, dan hanya setelah MAR paket itu disetujui. Selama belum ada satu pun PO, tidak ada pabrikasi yang berjalan di paket mana pun."
          action={
            <button className="btn btn-primary" onClick={() => nav('/packages')}>
              Buka daftar paket
            </button>
          }
        />
      ) : shown.length === 0 ? (
        <Empty title="Tidak ada PO yang cocok" body="Longgarkan penyaringnya." />
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>PO</th>
                <th>Paket</th>
                <th>Pemasok</th>
                <th>Terbit</th>
                <th>Status</th>
                <th>Dipesan</th>
                <th>Terkirim</th>
                <th>Kurang</th>
                <th>Surat jalan</th>
                <th>Terobosan</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((o) => (
                <tr key={o.id} onClick={() => nav(`/packages/${o.packageId}`)}>
                  <td className="num">{o.poNo}</td>
                  <td>{o.packageCode}</td>
                  <td>{o.supplierName ?? '—'}</td>
                  <td>{o.issuedOn ? fmtDate(o.issuedOn) : '—'}</td>
                  <td>
                    <span className={`chip ${PO_CHIP[o.state] ?? ''}`}>
                      {PO_STATE[o.state] ?? o.state}
                    </span>
                  </td>
                  <td>{o.qty > 0 ? qtyOf(o.qty, o.unit) : '—'}</td>
                  <td>{qtyOf(o.deliveredQty, o.unit)}</td>
                  <td className={o.qty > 0 && o.shortfallQty > 0 ? 'neg' : ''}>
                    {o.qty > 0 ? qtyOf(o.shortfallQty, o.unit) : '—'}
                  </td>
                  <td>{o.noteCount}</td>
                  <td>{o.overridden ? (o.overrideReason ?? 'tanpa alasan tercatat') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
